import { Collapse, Divider, Radio } from '@mui/material'
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import NewIcon from 'ui/atoms/NewIcon/NewIcon'
import {
  getOptimizationTypeOptionDisplayLabel,
  OptimizationSubtypeOption,
} from '../DigitalTwinCollectedSettingsView.helper'

type OptimizationSubtypeSelectorProps = {
  options: OptimizationSubtypeOption[]
  selectedOptionId: string | null
  onChange: (optionId: string) => void
}

export default function OptimizationSubtypeSelector({
  options,
  selectedOptionId,
  onChange,
}: OptimizationSubtypeSelectorProps): ReactElement {
  const { t } = useTranslation()
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [expanded, setExpanded] = useState(false)
  const [checkedOptionId, setCheckedOptionId] = useState<string | null>(selectedOptionId)
  const [previousCheckedOptionId, setPreviousCheckedOptionId] = useState<string | null>(selectedOptionId)
  const disabled = options.length === 0
  const checkedOption = options.find((option) => option.id === checkedOptionId)

  useEffect(() => {
    setCheckedOptionId(selectedOptionId)
  }, [selectedOptionId])

  const hasChanges = useMemo(
    () => checkedOptionId !== previousCheckedOptionId,
    [checkedOptionId, previousCheckedOptionId]
  )

  const handleToggleDropdown = useCallback(() => {
    if (disabled) {
      return
    }

    if (expanded) {
      setCheckedOptionId(selectedOptionId)
    } else {
      setPreviousCheckedOptionId(checkedOptionId)
    }

    setExpanded(!expanded)
  }, [checkedOptionId, disabled, expanded, selectedOptionId])

  const handleApply = useCallback(() => {
    if (checkedOptionId && hasChanges) {
      onChange(checkedOptionId)
    }
    setExpanded(false)
  }, [checkedOptionId, hasChanges, onChange])

  useEffect(() => {
    if (!expanded) return

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleApply()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [expanded, handleApply])

  useEffect(() => {
    if (!expanded) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleApply()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [expanded, handleApply])

  return (
    <div ref={dropdownRef} className="relative self-center">
      <button
        type="button"
        aria-expanded={expanded}
        aria-disabled={disabled}
        className={`flex flex-row items-center justify-between border-gray-300 w-72 h-10 border ${expanded ? 'rounded-t-md border-b-0' : 'rounded-md'} p-2 bg-white ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        onClick={handleToggleDropdown}
      >
        <div className="mx-1 flex flex-row justify-between w-full gap-2">
          <span>{checkedOption ? getOptimizationTypeOptionDisplayLabel(checkedOption) : t('Optimization type')}</span>
        </div>
        <NewIcon icon={expanded ? 'fal fa-chevron-down' : 'fal fa-chevron-right'} color="black" ariaLabel="expand" />
      </button>

      <Collapse in={expanded} timeout="auto">
        <div className="flex flex-col w-72 bg-white border border-gray-300 rounded-b-md border-t-0 p-2 absolute z-10">
          <div className="overflow-y-auto grow max-h-[40vh]">
            {options.map((option) => (
              <div
                key={option.id}
                role="radio"
                tabIndex={0}
                aria-checked={checkedOptionId === option.id}
                aria-label={getOptimizationTypeOptionDisplayLabel(option)}
                className={`flex flex-row items-center justify-between gap-2 rounded-md ${option.subtype ? 'pl-6' : 'pl-1.5'} my-1 py-1 hover:bg-gray-100 hover:cursor-pointer`}
                onClick={() => setCheckedOptionId(option.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    setCheckedOptionId(option.id)
                  }
                }}
              >
                <span className="flex-1 whitespace-normal wrap-break-word leading-snug">{option.label}</span>
                <Radio
                  checked={checkedOptionId === option.id}
                  onClick={(e) => {
                    e.stopPropagation()
                    setCheckedOptionId(option.id)
                  }}
                />
              </div>
            ))}
          </div>

          <Divider />

          <div className="flex flex-row items-center justify-end bg-white sticky bottom-0 pt-2">
            <NewButton label={t('Apply')} onClick={handleApply} disabled={!checkedOptionId} />
          </div>
        </div>
      </Collapse>
    </div>
  )
}
